import React, { useState } from 'react';
import { Card, ProgressBar } from 'react-bootstrap';
import { LevelProgress } from '../../../api/API';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import './LevelProgressCard.css';

interface LevelProgressCardProps {
  levelProgress: LevelProgress;
  displayMode: 'current' | 'graduated';
}

const getYouTubeEmbedUrl = (url: string): string | null => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/
  );
  if (!match) return null;
  const videoId = match[1];
  // playlist={id} is required for loop=1 to work on a single video
  return `https://www.youtube.com/embed/${videoId}?loop=1&playlist=${videoId}&playsinline=1&rel=0`;
};

const LevelProgressCard: React.FC<LevelProgressCardProps> = ({ levelProgress, displayMode }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedTopicId, setExpandedTopicId] = useState<number | null>(null);
  // Only used for topics that have a video: the description starts hidden there
  const [showDescription, setShowDescription] = useState(false);
  const {
    promotion,
    syllabusTopics,
    historySyllabusTopics,
    currentSyllabusTopics,
    totalTopics,
    learnedTopics,
    progressPercentage,
    historyTotalTopics,
    historyLearnedTopics,
    historyProgressPercentage
  } = levelProgress;

  // Determine which syllabus to display based on displayMode
  // For 'current' mode, show next level syllabus (currentSyllabusTopics)
  // For 'graduated' mode (history), show current level syllabus (historySyllabusTopics) if it exists
  const displayTopics = displayMode === 'current'
    ? (currentSyllabusTopics || syllabusTopics)
    : (historySyllabusTopics || syllabusTopics);

  // Use appropriate progress values based on displayMode
  const displayTotalTopics = displayMode === 'current' ? totalTopics : (historyTotalTopics ?? totalTopics);
  const displayLearnedTopics = displayMode === 'current' ? learnedTopics : (historyLearnedTopics ?? learnedTopics);
  const displayProgressPercentage = displayMode === 'current' ? progressPercentage : (historyProgressPercentage ?? progressPercentage);

  const showProgress = displayMode === 'current' && displayTotalTopics > 0;
  const toggleExpand = () => {
    if (showProgress) setIsExpanded(!isExpanded);
  };

  return (
    <Card className="level-progress-card mb-3">
      <Card.Header
        className="level-card-header"
        style={{ borderLeftColor: displayMode === 'current' ? promotion.nextLevelColor : promotion.levelColor }}
        onClick={toggleExpand}
      >
        <div className="level-header-content">
          <div className="level-info">
            <h4 className="level-name">
              {promotion.styleName} - {displayMode === 'current' ? promotion.nextLevelName : promotion.levelName}
            </h4>
            <div className="level-meta">
              <span className="achieved-date">
                {displayMode === 'current' ? (
                  <>Current level</>
                ) : (
                  <>Graduated: {moment(promotion.achievedAt).format('MMM DD, YYYY')}</>
                )}
              </span>
            </div>
          </div>
          {showProgress && (
            <div className="level-stats">
              <div className="progress-summary">
                <span className="progress-fraction">{displayLearnedTopics} / {displayTotalTopics}</span>
                <span className="progress-percentage">{displayProgressPercentage.toFixed(1)}%</span>
              </div>
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
                className="expand-icon"
              />
            </div>
          )}
        </div>
        {showProgress && (
          <ProgressBar
            now={displayProgressPercentage}
            variant="success"
            className="level-progress-bar"
          />
        )}
      </Card.Header>

      {showProgress && isExpanded && (
        <Card.Body className="level-card-body">
          <h5 className="topics-title">Syllabus Topics ({displayTopics.length})</h5>
          <div className="topics-list">
            {displayTopics.map((topic) => {
              const hasDetails = topic.isLearned && !!(topic.topicDescription || topic.videoLink);
              const isTopicExpanded = hasDetails && expandedTopicId === topic.id;
              const embedUrl = topic.videoLink ? getYouTubeEmbedUrl(topic.videoLink) : null;
              return (
                <div
                  key={topic.id}
                  className={`topic-item ${topic.isLearned ? 'learned' : 'not-learned'}${hasDetails ? ' expandable' : ''}`}
                >
                  <div
                    className="topic-header"
                    onClick={
                      hasDetails
                        ? () => {
                            setExpandedTopicId(isTopicExpanded ? null : topic.id);
                            setShowDescription(false);
                          }
                        : undefined
                    }
                  >
                    <div className="topic-checkbox">
                      <FontAwesomeIcon
                        icon={topic.isLearned ? faCheckCircle : faCircle}
                        className={topic.isLearned ? 'check-icon learned' : 'check-icon'}
                      />
                    </div>
                    <div className="topic-name">{topic.topicName}</div>
                    {hasDetails && (
                      <FontAwesomeIcon
                        icon={isTopicExpanded ? faChevronUp : faChevronDown}
                        className="topic-expand-icon"
                      />
                    )}
                  </div>
                  {isTopicExpanded && (
                    <div className="topic-details">
                      {topic.topicDescription && (
                        topic.videoLink ? (
                          <>
                            <button
                              type="button"
                              className="topic-description-toggle"
                              onClick={() => setShowDescription(!showDescription)}
                            >
                              {showDescription ? 'Hide the description' : 'Show the description'}
                            </button>
                            {showDescription && (
                              <div className="topic-description">{topic.topicDescription}</div>
                            )}
                          </>
                        ) : (
                          <div className="topic-description">{topic.topicDescription}</div>
                        )
                      )}
                      {topic.videoLink && (
                        embedUrl ? (
                          <div className="topic-video-embed">
                            <iframe
                              src={embedUrl}
                              title={topic.topicName}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        ) : (
                          <a
                            href={topic.videoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="topic-video-link"
                          >
                            Watch Tutorial
                          </a>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

export default LevelProgressCard;
